import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldWarning, ShieldCheck, CircleNotch, Warning, ArrowRight } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OigBadge = ({ npi, isMainSection = false }) => {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!npi) return;
    let cancelled = false;

    const fetchOig = async () => {
      try {
        const res = await fetch(`${API_URL}/api/oig/check/${npi}`);
        if (!res.ok) throw new Error("fetch failed");
        const json = await res.json();
        if (cancelled) return;
        setStatus(json.status);
        setData(json.data);
        setLastUpdated(json.last_updated);
      } catch {
        if (!cancelled) setStatus("unavailable");
      }
    };
    fetchOig();
    return () => { cancelled = true; };
  }, [npi]);

  if (status === "loading") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="oig-section">
        <h3 className={`font-display font-semibold text-slate-900 ${isMainSection ? "text-lg mb-4" : "text-base mb-3"}`}>
          OIG Exclusion Check
        </h3>
        <div className="flex items-center gap-2 text-slate-400">
          <CircleNotch className="w-5 h-5 animate-spin" />
          <span className="text-sm">Checking OIG exclusion list...</span>
        </div>
      </div>
    );
  }

  if (status === "unavailable" || status === "data_not_loaded") {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="oig-section">
        <h3 className={`font-display font-semibold text-slate-900 ${isMainSection ? "text-lg mb-4" : "text-base mb-3"}`}>
          OIG Exclusion Check
        </h3>
        <div className="flex items-center gap-2 text-slate-400">
          <Warning className="w-5 h-5" />
          <span className="text-sm">
            {status === "data_not_loaded" ? "Exclusion data is loading. Check back shortly." : "OIG status unavailable"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="oig-section">
      <h3 className={`font-display font-semibold text-slate-900 ${isMainSection ? "text-lg mb-4" : "text-base mb-3"}`}>
        OIG Exclusion Check
      </h3>

      {status === "excluded" ? (
        <div>
          <div className="flex items-center gap-2 mb-3" data-testid="oig-excluded-badge">
            <div className="flex items-center gap-1.5 bg-red-50 border border-red-300 rounded-full px-3 py-1.5">
              <ShieldWarning className="w-4 h-4 text-red-600" weight="fill" />
              <span className="text-sm font-semibold text-red-700">EXCLUDED</span>
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
            <p className="text-sm text-red-800 font-medium mb-1">
              This provider appears on the OIG List of Excluded Individuals/Entities (LEIE).
            </p>
            <p className="text-xs text-red-600">
              Federal healthcare programs (Medicare, Medicaid, etc.) cannot pay for items or services furnished by excluded individuals.
            </p>
          </div>
          {data && (
            <div className="space-y-1.5 text-sm">
              {data.excltype_desc && (
                <p className="text-slate-700">
                  <span className="font-medium text-slate-500">Reason:</span>{" "}
                  <span className="text-red-700 font-medium">{data.excltype_desc}</span>
                </p>
              )}
              {data.excldate && (
                <p className="text-slate-700">
                  <span className="font-medium text-slate-500">Exclusion Date:</span>{" "}
                  {data.excldate}
                </p>
              )}
              {data.general && (
                <p className="text-slate-700">
                  <span className="font-medium text-slate-500">Category:</span>{" "}
                  {data.general}
                </p>
              )}
              {data.state && (
                <p className="text-slate-700">
                  <span className="font-medium text-slate-500">State:</span>{" "}
                  {data.state}
                </p>
              )}
            </div>
          )}
          {isMainSection && (
            <div className="mt-3 pt-3 border-t border-red-100">
              <Link
                to="/oig-exclusion-lookup"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700 hover:text-red-800 transition-colors"
                data-testid="oig-detail-link"
              >
                Search OIG Exclusion Database <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-2" data-testid="oig-clear-badge">
            <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-600" weight="fill" />
              <span className="text-sm font-semibold text-emerald-700">Not Excluded</span>
            </div>
          </div>
          <p className="text-sm text-slate-500">
            This NPI does not appear on the OIG List of Excluded Individuals/Entities.
          </p>
          {isMainSection && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <Link
                to="/oig-exclusion-lookup"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal hover:text-teal/80 transition-colors"
                data-testid="oig-detail-link"
              >
                Search OIG Exclusion Database <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-3 border-t border-slate-100 pt-2">
        Data from HHS OIG LEIE database.{lastUpdated ? ` Last refreshed ${new Date(lastUpdated).toLocaleDateString("en-US", { month: "short", year: "numeric" })}.` : ""} Updated monthly.
      </p>
    </div>
  );
};

export default OigBadge;
